import { Controller } from "@hotwired/stimulus"

// Combined Payment + Xero filter popover (multi-select). Checkboxes across both
// axes accumulate while the <details> popover is open (no navigation per tick,
// so the florist isn't bounced out — the client complaint about the status
// filter). Closing the popover — via its summary or an outside click — applies
// the whole selection with a single events_list frame navigation, keeping scroll
// and advancing the URL. Each checkbox carries data-param (payment_filter /
// xero_filter) naming the axis it belongs to.
export default class extends Controller {
  static values = { frame: { type: String, default: "events_list" } }

  connect() {
    this.onToggle = this.onToggle.bind(this)
    this.onOutsideClick = this.onOutsideClick.bind(this)
    this.element.addEventListener("toggle", this.onToggle)
    document.addEventListener("click", this.onOutsideClick)
  }

  disconnect() {
    this.element.removeEventListener("toggle", this.onToggle)
    document.removeEventListener("click", this.onOutsideClick)
  }

  // Native <details> toggles on summary click; apply the picks when it closes,
  // unless the close was triggered by another events_list navigation (below).
  onToggle() {
    if (this.element.open) return
    if (this.skipApply) {
      this.skipApply = false
      return
    }
    this.apply()
  }

  // <details> won't close on an outside click by itself — do it here so the
  // selection applies without hunting back for the summary. But if the click is
  // itself an events_list navigation (a segment/sort link), let THAT win: close
  // without applying, so we don't fire a second frame nav that races it and
  // clobbers its change with our stale window.location.
  onOutsideClick(event) {
    if (!this.element.open || this.element.contains(event.target)) return
    this.skipApply = Boolean(event.target.closest("[data-turbo-frame='events_list']"))
    this.element.open = false // fires the toggle event → apply() (unless skipped)
  }

  apply() {
    const boxes = this.boxes
    const params = [...new Set(boxes.map((box) => box.dataset.param))]
    const url = new URL(window.location.href)
    let changed = false

    params.forEach((param) => {
      const checked = boxes.filter((box) => box.dataset.param === param && box.checked).map((box) => box.value)
      // Compare against both the array and any legacy scalar form of the param.
      const current = [...url.searchParams.getAll(`${param}[]`), ...url.searchParams.getAll(param)]
      if (!this.sameSet(checked, current)) changed = true

      url.searchParams.delete(`${param}[]`)
      url.searchParams.delete(param)
      checked.forEach((value) => url.searchParams.append(`${param}[]`, value))
    })

    if (!changed) return
    this.navigate(url.pathname + url.search)
  }

  // Drive the events_list frame through a real link so the navigation promotes
  // to a Turbo advance (address bar updates, filter stays shareable), exactly
  // like the segment filter links — a bare frame.src set does not advance.
  navigate(href) {
    if (!document.getElementById(this.frameValue)) {
      window.location.href = href
      return
    }
    const link = document.createElement("a")
    link.href = href
    link.dataset.turboFrame = this.frameValue
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  get boxes() {
    return Array.from(this.element.querySelectorAll("input[type='checkbox'][data-param]"))
  }

  sameSet(a, b) {
    if (a.length !== b.length) return false
    const sortedB = b.slice().sort()
    return a.slice().sort().every((value, i) => value === sortedB[i])
  }
}
